import React, { useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Users, Crown, Mail, Ban } from "lucide-react";

export default function TeamsPanel({ teams, onChange }) {
  const [expanded, setExpanded] = useState(null);   // team_id currently expanded
  const [details, setDetails] = useState({});       // { [team_id]: {members, pending_invites} }
  const [loadingId, setLoadingId] = useState(null);
  const [dqBusy, setDqBusy] = useState(null);

  const toggleDisqualify = async (e, t) => {
    e.stopPropagation();
    const dq = t.status === "disqualified";
    if (!dq && !window.confirm(`Disqualify team "${t.name}"? It will be excluded from judging and the leaderboard.`)) return;
    setDqBusy(t.id);
    try {
      await api.post(`/admin/teams/${t.id}/disqualify`, { enabled: !dq });
      toast.success(!dq ? `"${t.name}" disqualified` : `"${t.name}" reinstated`);
      onChange?.();
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setDqBusy(null); }
  };

  const toggle = async (teamId) => {
    if (expanded === teamId) { setExpanded(null); return; }
    setExpanded(teamId);
    if (!details[teamId]) {
      setLoadingId(teamId);
      try {
        const { data } = await api.get(`/admin/teams/${teamId}/members`);
        setDetails(d => ({ ...d, [teamId]: data }));
      } catch (err) { toast.error(errorMessage(err)); }
      finally { setLoadingId(null); }
    }
  };

  if (teams.length === 0) {
    return <div className="card-surface text-center text-gray-500" data-testid="teams-empty">No teams yet.</div>;
  }

  return (
    <div className="space-y-2" data-testid="teams-panel">
      <div className="card-surface overflow-hidden" data-testid="teams-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-mono text-left border-b border-white/10">
              <th className="py-3 pl-2 pr-4 w-8"></th>
              <th className="py-3 pr-4">Team Name</th>
              <th className="py-3 pr-4">Leader</th>
              <th className="py-3 pr-4">Members</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3 pr-4">Created</th>
              <th className="py-3 pr-4">Moderation</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => {
              const isOpen = expanded === t.id;
              const d = details[t.id];
              return (
                <React.Fragment key={t.id}>
                  <tr
                    className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                    onClick={() => toggle(t.id)}
                    data-testid={`team-row-${t.id}`}
                  >
                    <td className="py-3 pl-2 pr-4 text-gray-400">
                      {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </td>
                    <td className="py-3 pr-4 text-white">{t.name}</td>
                    <td className="py-3 pr-4 text-gray-400">{t.leader_name}</td>
                    <td className="py-3 pr-4 text-white">{t.member_count}</td>
                    <td className="py-3 pr-4"><span className={`badge badge-${t.status}`}>{t.status}</span></td>
                    <td className="py-3 pr-4 text-gray-500 font-mono text-xs">{new Date(t.created_at).toLocaleString()}</td>
                    <td className="py-3 pr-4">
                      <button
                        onClick={(e) => toggleDisqualify(e, t)}
                        disabled={dqBusy === t.id}
                        className={`!py-1 !px-2.5 text-xs ${t.status === "disqualified" ? "btn-secondary" : "btn-danger"} inline-flex items-center gap-1`}
                        data-testid={`team-disqualify-toggle-${t.id}`}>
                        {dqBusy === t.id ? <span className="spinner" /> : (t.status === "disqualified" ? "Reinstate" : <><Ban className="w-3 h-3" /> Disqualify</>)}
                      </button>
                    </td>
                  </tr>

                  {isOpen && (
                    <tr className="bg-white/[0.015]" data-testid={`team-details-${t.id}`}>
                      <td colSpan={7} className="py-5 px-6 border-b border-white/10">
                        {loadingId === t.id && <div className="text-center py-6"><span className="spinner" /></div>}
                        {d && (
                          <div className="space-y-5">
                            <div>
                              <div className="label-mono mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5 text-brand" /> Accepted members · {d.members.filter(m => m.status === "accepted").length}</div>
                              <div className="grid sm:grid-cols-2 gap-3">
                                {d.members.map((m) => (
                                  <div key={m.user_id} className="border border-white/10 p-3 flex items-start gap-3" data-testid={`member-${m.user_id}`}>
                                    <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                                      {m.role === "leader"
                                        ? <Crown className="w-4 h-4 text-brand" strokeWidth={2} />
                                        : <Users className="w-4 h-4 text-gray-400" strokeWidth={2} />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2">
                                        <div className="font-display text-sm text-white truncate">{m.name}</div>
                                        {m.role === "leader" && <span className="text-[10px] font-mono uppercase tracking-widest text-brand">leader</span>}
                                        <span className={`badge badge-${m.status === "accepted" ? "accepted" : "invited"} text-[10px]`}>{m.status}</span>
                                      </div>
                                      <div className="text-xs text-gray-400 font-mono truncate" title={m.college_email}>{m.college_email}</div>
                                      {m.personal_email && m.personal_email !== m.college_email && (
                                        <div className="text-[11px] text-gray-500 font-mono truncate" title={m.personal_email}>{m.personal_email}</div>
                                      )}
                                      <div className="text-[11px] text-gray-500 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                        {m.college_name && <span>🏫 {m.college_name}</span>}
                                        <span className={m.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}>
                                          {m.payment_status === "paid" ? "✓ Paid" : "● Pending"}
                                        </span>
                                        {m.college_verified ? <span className="text-emerald-400">✓ Verified</span> : <span className="text-amber-400">● Unverified</span>}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {d.members.length === 0 && <div className="text-gray-500 text-sm">No members yet.</div>}
                              </div>
                            </div>

                            {d.pending_invites && d.pending_invites.length > 0 && (
                              <div>
                                <div className="label-mono mb-2 flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-brand" /> Outstanding email invites · {d.pending_invites.length}</div>
                                <div className="space-y-2">
                                  {d.pending_invites.map((inv) => (
                                    <div key={inv.token} className="border border-white/10 p-2.5 flex flex-wrap items-center justify-between gap-3 text-xs" data-testid={`invite-${inv.token}`}>
                                      <span className="font-mono text-gray-300">{inv.invited_email}</span>
                                      <span className="text-gray-500">
                                        Reminders sent: <span className="text-white">{inv.reminder_count || 0}</span>
                                        {inv.expires_at && <span className="ml-3">expires {new Date(inv.expires_at).toLocaleString()}</span>}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-gray-500 font-mono">// Click any team row to expand members and outstanding invites.</div>
    </div>
  );
}
